import React ,{ useState,useEffect } from "react";
import {
  Stack,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditIcon from "@mui/icons-material/Edit";
import { Controller } from "react-hook-form";
import ReuseableLabel from "../ReusableComponents/ReuseableLabel.jsx";

const BasicSection = ({
  control,
  errors,
  watch,
  setValue,
  register,
  isEditMode = false,
  defaultValues = {},
  PrettoSlider,
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
 
  const generateDescription = async () => {
    const question = watch("Question");
    if (!question) { alert("Enter question first"); return; }
    
    setIsGenerating(true);
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer YOUR_API_KEY'
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [{ role: 'user', content: `Generate description for: "${question}"` }],
          max_tokens: 100
        })
      });
      
      const data = await response.json();
      setValue("Description", data.choices[0].message.content);
    } catch (error) {
      console.error(error);
      alert("Failed to generate description");
    } finally {
      setIsGenerating(false);
    }
  };
  return (
    <Stack
      direction={"column"}
      gap={2}
      width={{
        md: "80%",
        lg: "80%",
        xl: "80%",
        sm: "100%",
        xs: "100%",
      }}
      margin={"0 auto"}
      mt={"20px"}
    >
      <Box>
        <Stack >
          {/* Yes/No Checkbox */}
          <FormControl component="fieldset">
            <Controller
              name="Bot Interpreted"
              control={control}
              defaultValue={defaultValues["Bot Interpreted"] || ""}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={
                        field.value === "y" && watch("Bot Name") === "yesOrNo"
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setValue("Bot Interpreted", checked ? "y" : "n", {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                        setValue("Bot Name", checked ? "yesOrNo" : "", {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                    />
                  }
                  label="Answer in Yes or No only"
                />
              )}
            />
          </FormControl>

    
          {/* Description Field */}
          
         <TextField
            fullWidth
            multiline
            size="medium"
            variant="outlined"
            label="Description"
            placeholder="Enter description or generate with AI"
            {...register("Description")}
            defaultValue={defaultValues.Description || ""}
            error={Boolean(errors.Description)}
            helperText={errors.Description?.message}
            sx={{ marginBottom: 2 }}
            InputProps={{
            endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
            <Tooltip title="Generate description with AI" arrow enterDelay={300}>
            <Button
            onClick={generateDescription}
            disabled={isGenerating}
            size="small"
            variant="text"
            sx={{
              minWidth: "auto",
              padding: "4px",
              borderRadius: "4px",
              opacity: 1,
              mt:1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
          <Box sx={{ position: "relative", width: 32, height: 32 }}>
      
      {/* AI icon */}
      <AutoAwesomeIcon
        sx={{
          fontSize: 26,
          color: "#b1b1b1",
        }}
      />

      {/* Pencil icon */}
      <EditIcon
        sx={{
          fontSize: 18,
          position: "absolute",
          bottom: 0,
          right: 0,
          color: "#b1b1b1",
          backgroundColor: "#fff",
          borderRadius: "50%",
          padding: "0.5px",
        }}
      />
            </Box>
           </Button>
          </Tooltip>
        </InputAdornment>
      ),
    }}
  />
      
          {/* Question Type */}
          <FormControl
            variant="outlined"
            size="small"
            error={Boolean(errors.questionType)}
          >
            <InputLabel id="demo-simple-select-required-label">
              <ReuseableLabel label={"Question Type"} />
            </InputLabel>
            <Controller
              name="questionType"
              control={control}
              defaultValue={defaultValues.questionType || ""}
              rules={{ required: "Please select an option" }}
              render={({ field }) => (
                <>
                  <Select
                    {...field}
                    labelId="demo-simple-select-required-label"
                    id="demo-simple-select-required"
                    label="Question Type*"
                    style={{ height: "43px" }}
                    error={Boolean(errors.questionType)}
                  >
                    <MenuItem value={""}>Please select an option</MenuItem>
                    <MenuItem value={"Default Question"}>
                      Default Question
                    </MenuItem>
                    <MenuItem value={"Generic Question"}>
                      Generic Question
                    </MenuItem>
                    <MenuItem value={"Profile Gap Question"}>
                      Profile Gap Question
                    </MenuItem>
                    <MenuItem value={"Assessment Question"}>
                      Assessment Question
                    </MenuItem>
                    <MenuItem value={"Job Specific Question"}>
                      Job Specific Question
                    </MenuItem>
                    <MenuItem value={"POFU Question"}>POFU Question</MenuItem>
                    <MenuItem value={"Offer Question"}>Offer Question</MenuItem>
                    <MenuItem value={"Onboarding Question"}>
                      Onboarding Question
                    </MenuItem>
                    <MenuItem value={"BlueCollar Question"}>
                      BlueCollar Question
                    </MenuItem>
                    <MenuItem value={"Document Collection Question"}>
                      Document Collection Question
                    </MenuItem>
                  </Select>
                  {errors.questionType && (
                    <FormHelperText error>
                      {errors.questionType.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </FormControl>

          {/* Mandatory Question */}
          <FormControl
            variant="outlined"
            style={{ marginTop: 11, minWidth: 150 }}
            size="small"
            error={Boolean(errors.mandatoryQuestion)}
          >
            <InputLabel id="demo-simple-select-required-label">
              Mandatory Question
            </InputLabel>
            <Controller
              name="mandatoryQuestion"
              control={control}
              defaultValue={defaultValues.mandatoryQuestion || ""}
              render={({ field }) => (
                <>
                  <Select
                    {...field}
                    labelId="mandatoryQuestion"
                    id="mandatoryQuestionId"
                    label="mandatoryQuestion"
                    style={{ height: "43px" }}
                    error={Boolean(errors.mandatoryQuestion)}
                  >
                    <MenuItem value={""}>Please select an option</MenuItem>
                    <MenuItem value={"yes"}>Yes</MenuItem>
                    <MenuItem value={"no"}>No</MenuItem>
                  </Select>
                  {errors.mandatoryQuestion && (
                    <FormHelperText error>
                      {errors.mandatoryQuestion.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </FormControl>

          {/* Question Expiry */}
          <FormControl
            variant="outlined"
            style={{ marginTop: 11, minWidth: 150 }}
            size="small"
            error={Boolean(errors.questionExpiry)}
          >
            <InputLabel id="demo-simple-select-required-label">
              <ReuseableLabel label={"Question Expiry"} />
            </InputLabel>
            <Controller
              name="questionExpiry"
              control={control}
              defaultValue={defaultValues.questionExpiry || ""}
              rules={{ required: "Please select an option" }}
              render={({ field }) => (
                <>
                  <Select
                    {...field}
                    labelId="questionExpiry"
                    id="questionExpiryId"
                    label="Question Expiry*"
                    style={{ height: "43px" }}
                    error={Boolean(errors.questionExpiry)}
                  >
                    <MenuItem value={""}>Please select an option</MenuItem>
                    <MenuItem value={"never"}>Never</MenuItem>
                    <MenuItem value={"15"}>15 days</MenuItem>
                    <MenuItem value={"30"}>30 days</MenuItem>
                    <MenuItem value={"90"}>90 days</MenuItem>
                    <MenuItem value={"180"}>180 days</MenuItem>
                    <MenuItem value={"365"}>365 days</MenuItem>
                  </Select>
                  {errors.questionExpiry && (
                    <FormHelperText error>
                      {errors.questionExpiry.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </FormControl>

          {/* Weight Slider */}
          <Controller
            name="Weight"
            control={control}
            defaultValue={defaultValues.Weight || 0.5}
            rules={{ required: "Weight is Required" }}
            render={({ field }) => (
              <Box
                sx={{
                  marginTop: 1,
                  minWidth: 150,
                  border: "1px solid #908E8E",
                  borderRadius: "7px",
                  height: "43px",
                }}
              >
                <Stack direction="row" spacing={2} padding={1.2}>
                  <Typography
                    sx={{
                      paddingTop: "1px",
                      paddingLeft: "5px",
                      opacity: 0.7,
                      display: "flex",
                      gap: "2px",
                    }}
                  >
                    Weightage <span style={{ color: "red" }}> *</span>
                  </Typography>

                  <PrettoSlider
                    {...field}
                    value={Number(field.value) ?? 0.1}
                    onChange={(_, value) => field.onChange(value)}
                    valueLabelDisplay="auto"
                    min={0.1}
                    step={0.1}
                    max={1}
                    sx={{ width: "80%", marginLeft: "8%" }}
                  />
                </Stack>

                {errors.Weight && (
                  <Typography color="error" fontSize={12} pl={2}>
                    {errors.Weight.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Stack>
      </Box>
    </Stack>
  );
};

export default BasicSection;
